<template>
  <div
    v-if="loading"
    role="status"
    class="flex gap-0.5 h-24 items-end overflow-hidden animate-pulse"
  >
    <div
      v-for="(percent, key) in loadingBars"
      :key
      class="w-full bg-bgr-200"
      :style="{ height: `${percent}%` }"
    ></div>
  </div>

  <div v-else class="relative">
    <!-- 
      Chart.js uses its parent container to update the canvas render and display sizes.
      However, this method requires the container to be relatively positioned and dedicated to the chart canvas only.
      See https://www.chartjs.org/docs/latest/configuration/responsive.html#important-note
    -->
    <Bar :data :options="BAR_OPTIONS" class="!w-full" />
  </div>
</template>

<script setup lang="ts">
import { BarElement, CategoryScale, Chart, LinearScale } from 'chart.js'
import { type ChartProps, Bar } from 'vue-chartjs'

Chart.register(BarElement, CategoryScale, LinearScale)

type Item = { value: NumInterval; count?: number }

const BAR_OPTIONS: ChartProps<'bar'>['options'] = {
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
  scales: {
    x: { display: false },
    y: { display: false, beginAtZero: true },
  },
}

const props = withDefaults(
  defineProps<{
    items: Item[]
    disabled?: boolean
    loading?: boolean
    loadingItems?: Partial<Item>[]
  }>(),
  {
    disabled: false,
    loadingItems: () =>
      Array.from({ length: 20 }, () => ({ count: Math.random() })),
  },
)

const model = defineModel<NumInterval>({ required: true })

const colors = useColors()

const loadingBars = computed(() => {
  const counts = props.loadingItems.map(({ count }) => count ?? 0)
  const max = Math.max(...counts)
  return counts.map((count) => Math.round((count / max) * 100))
})

const data = computed<ChartProps<'bar'>['data']>(() => ({
  labels: props.items.map(({ value }) => value.join(' - ')),

  datasets: [
    {
      data: props.items.map(({ count }) => count ?? 0),

      backgroundColor: props.items.map(({ value }) => {
        if (props.disabled) return colors.background200

        return (value[0] >= model.value[0] && value[0] <= model.value[1]) ||
          (model.value[0] >= value[0] && model.value[0] <= value[0]) ||
          (model.value[1] >= value[0] && model.value[1] <= value[0])
          ? colors.theme
          : colors.background200
      }),

      categoryPercentage: 1,
      barPercentage: 0.9,
    },
  ],
}))
</script>
